'use client'

import type { ReactNode } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode } from 'swiper/modules'

import 'swiper/css'

/** Maximum number of cards previewed in a dashboard carousel before "View all". */
export const DASHBOARD_CAROUSEL_MAX_ITEMS = 6

type DashboardCarouselProps<T> = {
  items: T[]
  getItemKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  /** Optional static test id applied to every slide. */
  slideTestId?: string
  /** Cap the number of previewed cards. Defaults to {@link DASHBOARD_CAROUSEL_MAX_ITEMS}. */
  maxItems?: number
}

/**
 * Shared horizontal card carousel for the account dashboard sections
 * (orders, consultations, expertises). Encapsulates the Swiper config so the
 * breakpoints, shadow spacing and equal-height behaviour stay consistent.
 */
export function DashboardCarousel<T>({ items, getItemKey, renderItem, slideTestId, maxItems = DASHBOARD_CAROUSEL_MAX_ITEMS }: DashboardCarouselProps<T>) {
  const visibleItems = items.slice(0, maxItems)

  return (
    <div className="w-full">
      <Swiper
        className="w-full"
        wrapperClass="w-auto px-5 py-6"
        cssMode
        slidesPerView={1.2}
        spaceBetween={30}
        breakpoints={{
          640: { slidesPerView: 1.5 },
          768: { slidesPerView: 2.1 },
          1024: { slidesPerView: 3.2 },
        }}
        freeMode
        modules={[FreeMode]}
      >
        {visibleItems.map((item) => (
          <SwiperSlide key={getItemKey(item)} className="flex !h-auto" data-testid={slideTestId}>
            {renderItem(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
